// Thin fetch wrapper. All calls are same-origin relative paths (`/api/...`) so this works identically
// in dev (proxied by Vite to the Express backend) and in production (the backend serves this app's
// built assets from the same origin) — never a hardcoded host, never localhost from browser code.
import type {
  AuditLogEntry,
  BalanceResponse,
  ConflictExplanation,
  EntityExtractionResult,
  EvaluateResponse,
  EvaluationHistoryItem,
  IsolationCertificateSummary,
  IsolationPoint,
  Lockbox,
  Organization,
  OverrideRecord,
  PermitDetail,
  PermitListItem,
  PermitStatus,
  PermitType,
  PlantEdge,
  PlantNode,
  PlantSummary,
  SubscriptionResponse,
  UsageEvent,
  User,
  UserRole,
} from './types';
import { captureGeolocation } from './geolocation';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const isFormData = init?.body instanceof FormData;
  const res = await fetch(path, {
    credentials: 'include',
    // FormData bodies must NOT get an explicit Content-Type: the browser needs to set its own
    // multipart boundary, which it can only do if this header is left unset.
    headers: init?.body && !isFormData ? { 'Content-Type': 'application/json', ...init.headers } : init?.headers,
    ...init,
  });

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const message = (body && (body.error as string)) || res.statusText || 'Request failed';
    throw new ApiError(res.status, message, body?.details);
  }
  return body as T;
}

const get = <T>(path: string) => request<T>(path);
const postForm = <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', body: formData });
const post = <T>(path: string, data?: unknown) => request<T>(path, { method: 'POST', body: data !== undefined ? JSON.stringify(data) : undefined });
const patch = <T>(path: string, data?: unknown) => request<T>(path, { method: 'PATCH', body: data !== undefined ? JSON.stringify(data) : undefined });
const del = <T>(path: string, data?: unknown) => request<T>(path, { method: 'DELETE', body: data !== undefined ? JSON.stringify(data) : undefined });

// ---------------------------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------------------------
export const authApi = {
  me: () => get<{ user: User; org: Organization | null }>('/api/auth/me'),
  providers: () => get<{ logtoConfigured: boolean; demoLoginAvailable: boolean }>('/api/auth/providers'),
  loginInfo: () => get<{ logtoConfigured: boolean; demoLoginAvailable?: boolean; hint?: string }>('/api/auth/login'),
  demoLogin: (role: UserRole) => post<{ user: User }>('/api/auth/demo-login', { role }),
  logout: () => post<{ logoutUrl: string | null }>('/api/auth/logout'),
};

// ---------------------------------------------------------------------------------------------
// Plants
// ---------------------------------------------------------------------------------------------
export const plantApi = {
  list: () => get<{ plants: PlantSummary[] }>('/api/plants'),
  get: (plantId: string) => get<{ plant: PlantSummary }>(`/api/plants/${plantId}`),
  topology: (plantId: string) => get<{ nodes: PlantNode[]; edges: PlantEdge[] }>(`/api/plants/${plantId}/topology`),
  create: (input: { name: string; code: string; description?: string; timezone?: string }) =>
    post<{ plant: PlantSummary }>('/api/plants', input),
  createWorkLocation: (plantId: string, input: { name: string; x: number; y: number; z?: number; areaId?: string; elevationLevelId?: string }) =>
    post<{ workLocationId: string }>(`/api/plants/${plantId}/work-locations`, input),
};

// ---------------------------------------------------------------------------------------------
// Permits
// ---------------------------------------------------------------------------------------------
export const permitApi = {
  list: (plantId: string, status?: PermitStatus[]) =>
    get<{ permits: PermitListItem[] }>(`/api/plants/${plantId}/permits${status?.length ? `?status=${status.join(',')}` : ''}`),
  get: (plantId: string, permitId: string) => get<{ permit: PermitDetail; latestEvaluation: EvaluationHistoryItem | null }>(`/api/plants/${plantId}/permits/${permitId}`),
  create: (
    plantId: string,
    input: {
      permitNumber: string;
      permitType: PermitType;
      scopeText: string;
      workLocationId?: string;
      primaryNodeId?: string;
      performingCompany?: string;
      plannedStart: string;
      plannedEnd: string;
      taggedNodeIds?: string[];
    },
  ) => post<{ permitId: string }>(`/api/plants/${plantId}/permits`, input),
  evaluate: (plantId: string, permitId: string) => post<EvaluateResponse>(`/api/plants/${plantId}/permits/${permitId}/evaluate`),
  issue: async (plantId: string, permitId: string) => {
    const geo = await captureGeolocation();
    return post<{ status: string; evaluation: EvaluationHistoryItem }>(`/api/plants/${plantId}/permits/${permitId}/issue`, geo ? { geo } : {});
  },
  close: (plantId: string, permitId: string) => post<{ status: string }>(`/api/plants/${plantId}/permits/${permitId}/close`),
  explain: (plantId: string, permitId: string, conflictId: string) => get<ConflictExplanation>(`/api/plants/${plantId}/permits/${permitId}/conflicts/${conflictId}/explain`),
  recordGasTest: (
    plantId: string,
    permitId: string,
    input: { o2Pct?: number; lelPct?: number; h2sPpm?: number; toxicPpm?: number; result: 'PASS' | 'FAIL'; validForMinutes?: number },
  ) => post<{ gasTestId: string; expiresAt: string }>(`/api/plants/${plantId}/permits/${permitId}/gas-tests`, input),
  extractTags: (plantId: string, permitId: string, scopeText?: string) =>
    post<EntityExtractionResult>(`/api/plants/${plantId}/permits/${permitId}/extract-tags`, { scopeText }),
  confirmTags: (plantId: string, permitId: string, tags: Array<{ nodeId: string; source: 'MANUAL' | 'AI_EXTRACTED'; confidence?: number }>) =>
    post<{ ok: true }>(`/api/plants/${plantId}/permits/${permitId}/tags`, { tags }),
};

// ---------------------------------------------------------------------------------------------
// Conflicts / Overrides
// ---------------------------------------------------------------------------------------------
export const conflictApi = {
  listOpen: (plantId: string) => get<{ evaluations: Array<EvaluationHistoryItem & { permitId: string; permitNumber: string }> }>(`/api/plants/${plantId}/conflicts`),
  listOverrides: (plantId: string) => get<{ overrides: OverrideRecord[] }>(`/api/plants/${plantId}/conflicts/overrides`),
  requestOverride: (plantId: string, conflictId: string, input: { justificationText: string; mitigationControl: string; evidencePhoto?: File | null }) => {
    const form = new FormData();
    form.set('justificationText', input.justificationText);
    form.set('mitigationControl', input.mitigationControl);
    if (input.evidencePhoto) form.set('evidencePhoto', input.evidencePhoto);
    return postForm<{ overrideId: string; status: string }>(`/api/plants/${plantId}/conflicts/${conflictId}/overrides`, form);
  },
  coAuthorize: async (plantId: string, overrideId: string, approve: boolean) => {
    const geo = await captureGeolocation();
    return post<{ status: string }>(`/api/plants/${plantId}/conflicts/overrides/${overrideId}/co-authorize`, geo ? { approve, geo } : { approve });
  },
};

// ---------------------------------------------------------------------------------------------
// Isolation
// ---------------------------------------------------------------------------------------------
export const isolationApi = {
  list: (plantId: string) => get<{ certificates: IsolationCertificateSummary[] }>(`/api/plants/${plantId}/isolation-certificates`),
  create: (plantId: string, input: { certificateNumber: string; description?: string }) =>
    post<{ certificateId: string }>(`/api/plants/${plantId}/isolation-certificates`, input),
  addPoint: (plantId: string, certId: string, input: { nodeId: string; isolationType: string; requiredState: string }) =>
    post<{ isolationPointId: string }>(`/api/plants/${plantId}/isolation-certificates/${certId}/points`, input),
  points: (plantId: string, certId: string) => get<{ points: IsolationPoint[] }>(`/api/plants/${plantId}/isolation-certificates/${certId}/points`),
  verifyPoint: async (plantId: string, certId: string, pointId: string) => {
    const geo = await captureGeolocation();
    return post<{ ok: true }>(`/api/plants/${plantId}/isolation-certificates/${certId}/points/${pointId}/verify`, geo ? { geo } : {});
  },
  lockboxes: (plantId: string, certId: string) => get<{ lockboxes: Lockbox[] }>(`/api/plants/${plantId}/isolation-certificates/${certId}/lockboxes`),
  createLockbox: (plantId: string, certId: string, name: string) => post<{ lockboxId: string }>(`/api/plants/${plantId}/isolation-certificates/${certId}/lockboxes`, { name }),
  applyLock: (plantId: string, certId: string, lockboxId: string, permitId: string) =>
    post<{ lockId: string }>(`/api/plants/${plantId}/isolation-certificates/${certId}/lockboxes/${lockboxId}/locks`, { permitId }),
  releaseLock: (plantId: string, certId: string, lockboxId: string, lockId: string, permitId: string) =>
    del<{ ok: true }>(`/api/plants/${plantId}/isolation-certificates/${certId}/lockboxes/${lockboxId}/locks/${lockId}`, { permitId }),
  releaseCertificate: (plantId: string, certId: string) => post<{ status: string }>(`/api/plants/${plantId}/isolation-certificates/${certId}/release`),
};

// ---------------------------------------------------------------------------------------------
// Billing / Marketplace
// ---------------------------------------------------------------------------------------------
export const billingApi = {
  subscription: () => get<SubscriptionResponse>('/api/billing/subscription'),
  balance: () => get<BalanceResponse>('/api/billing/balance'),
  usageEvents: (limit?: number) => get<{ events: UsageEvent[] }>(`/api/billing/usage-events${limit ? `?limit=${limit}` : ''}`),
};

// ---------------------------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------------------------
export const adminApi = {
  orgs: () => get<{ orgs: Organization[] }>('/api/admin/orgs'),
  setPlan: (orgId: string, input: { planTier: string; maxActivePermits: number; maxPlants: number }) =>
    patch<{ ok: true }>(`/api/admin/orgs/${orgId}/plan`, input),
  users: () => get<{ users: Array<User & { orgName?: string | null }> }>('/api/admin/users'),
  setUserRole: (userId: string, role: UserRole) => patch<{ ok: true }>(`/api/admin/users/${userId}/role`, { role }),
  setUserStatus: (userId: string, status: 'active' | 'suspended') => patch<{ ok: true }>(`/api/admin/users/${userId}/status`, { status }),
  auditLog: (params?: { plantId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.plantId) qs.set('plantId', params.plantId);
    if (params?.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get<{ entries: AuditLogEntry[] }>(`/api/admin/audit-log${suffix}`);
  },
  verifyAuditChain: () => get<{ valid: boolean; entriesChecked: number; firstBrokenSeq: number | null; reason: string | null }>('/api/admin/audit-log/verify'),
};

export { del as deleteRequest };
